import { useEffect, useState } from "react";
import { Fingerprint, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { biometricAvailable, registerPasskey, takePasskeyOffer, skipKey } from "@/lib/passkey";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// One-time offer after a password login/registration to bind Face ID / fingerprint.
export const PasskeyPrompt = ({ accent = "bg-[#1E3A8A] hover:bg-[#152a63]" }) => {
  const { master, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Per account + staff profile, so each team member on a shared device gets their own offer.
  const acc = (master?.id || master?.email) && `${master?.id || master?.email}:${user?.staff_id || "store"}`;

  useEffect(() => {
    if (!acc || !takePasskeyOffer() || localStorage.getItem(skipKey(acc))) return;
    (async () => {
      if (!(await biometricAvailable())) return;
      const { data } = await api.get("/auth/passkey").catch(() => ({ data: [{}] }));
      if (!data.some((p) => (p.staff_id || null) === (user?.staff_id || null))) setOpen(true);
    })();
  }, [acc]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    setBusy(true);
    try { await registerPasskey(""); toast.success("Done — next time just use Face ID / fingerprint"); setOpen(false); }
    catch (e) { if (!e.cancelled) toast.error(e.message); } finally { setBusy(false); }
  };
  const never = () => { localStorage.setItem(skipKey(acc), "1"); setOpen(false); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="passkey-prompt" className="max-w-sm rounded-2xl">
        <DialogHeader className="items-center text-center">
          <span className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-1"><Fingerprint className="w-9 h-9" /></span>
          <DialogTitle className="text-lg font-extrabold">Sign in faster next time</DialogTitle>
          <DialogDescription>Use Face ID, fingerprint or your device screen lock instead of typing your password. Your password still works.</DialogDescription>
        </DialogHeader>
        <p className="flex items-center gap-1.5 text-[11px] text-slate-500 justify-center"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Your fingerprint/face never leaves this device.</p>
        <div className="space-y-2">
          <Button data-testid="passkey-prompt-enable" disabled={busy} onClick={add} className={`w-full h-12 ${accent} text-white font-bold gap-2`}><Fingerprint className="w-5 h-5" /> {busy ? "Waiting for your device…" : "Enable Face ID / Fingerprint"}</Button>
          <Button data-testid="passkey-prompt-later" variant="outline" onClick={() => setOpen(false)} className="w-full h-11 font-bold">Not now</Button>
          <button data-testid="passkey-prompt-never" onClick={never} className="w-full text-xs text-slate-400 hover:text-slate-600 pt-1">Don't ask again on this device</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
